import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import apiClient from '../api/apiClients';
import './FeedbackForm.css';

const FEEDBACK_CATEGORIES = [
  "I'd like to suggest additional models for the catalog",
  "I'd like to report a mistake I found within the catalog's content",
  "I'd like to offer suggestions to improve usability",
  "I'd like to report bug(s) or error(s) that I encountered while browsing the site"
];

const CATEGORY_QUESTIONS = {
  [FEEDBACK_CATEGORIES[0]]: "I suggest adding the following model(s) or content to the catalog (for a new model, please include the model name, a link, and a brief description):",
  [FEEDBACK_CATEGORIES[1]]: "I found a mistake within in the model catalog's content. I'd like to suggest the following corrections:",
  [FEEDBACK_CATEGORIES[2]]: "I believe the following change(s) would help users navigate the model catalog and improve the user's experience:",
  [FEEDBACK_CATEGORIES[3]]: "I would like to report the following bug(s) or error(s):",
};
const OTHER_FEEDBACK_QUESTION = "Please share any other feedback or input that will help us improve the model catalog:";

const FeedbackForm = ({ isOpen, onClose, userEmail }) => {
  const [formData, setFormData] = useState({
    email: userEmail || '',
    message: '',
    categories: [],
    browser: navigator.userAgent,
    url: window.location.href,
    name: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [categoryAnswers, setCategoryAnswers] = useState({});
  const [otherFeedback, setOtherFeedback] = useState("");

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: userEmail || '',
        categories: [],
        browser: navigator.userAgent,
        url: window.location.href,
        name: '',
      });
      setCategoryAnswers({});
      setOtherFeedback("");
      setSubmitStatus(null);
    }
  }, [isOpen, userEmail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (category) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
    }));
  };

  const handleCategoryAnswerChange = (category, value) => {
    setCategoryAnswers((prev) => ({ ...prev, [category]: value }));
  };

  const handleOtherFeedbackChange = (e) => {
    setOtherFeedback(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    // Build the qa_pairs array
    const qa_pairs = [];
    formData.categories.forEach((category) => {
      if (CATEGORY_QUESTIONS[category]) {
        qa_pairs.push({
          question: CATEGORY_QUESTIONS[category],
          answer: categoryAnswers[category] || ""
        });
      }
    });
    // Always add the other feedback at the end
    qa_pairs.push({
      question: OTHER_FEEDBACK_QUESTION,
      answer: otherFeedback || ""
    });
    // Remove categories and message from submitData
    const { categories, message, ...rest } = formData;
    const submitData = {
      ...rest,
      qa_pairs,
    };
    try {
      const response = await apiClient.postRequest('/feedback', submitData);
      if (response) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({
            email: userEmail || '',
            categories: [],
            browser: navigator.userAgent,
            url: window.location.href,
            name: '',
          });
          setCategoryAnswers({});
          setOtherFeedback("");
          setSubmitStatus(null);
        }, 2000);
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-overlay">
      <div className="feedback-content">
        <button className="feedback-close" onClick={onClose} aria-label="Close feedback form">
          <FaTimes />
        </button>
        <div>
          <h1 className="feedback-title">FEEDBACK FORM</h1>
          <p className="feedback-description">
            Please leave a comment or suggestion on how to improve the flood model catalog and its usability. We appreciate your input!
          </p>
          <form onSubmit={handleSubmit} className="feedback-form">
          <div className="form-group">
    <label className="feedback-checkbox-label">
      <span className="section-title">I would like to submit feedback regarding the following topics. Select all that apply:</span>
    </label>
    <div className="feedback-categories feedback-categories-vertical">
      {FEEDBACK_CATEGORIES.map((category) => (
        <label key={category} className="feedback-category">
          <input
            type="checkbox"
            checked={formData.categories.includes(category)}
            onChange={() => handleCategoryChange(category)}
            className="custom-checkbox"
            aria-checked={formData.categories.includes(category)}
          />
          <span className="checkbox-label">{category}</span>
        </label>
      ))}
    </div>
  </div>
  {/* Dynamic textareas for selected categories */}
  {formData.categories.map((category) => (
    <div className="form-group feedback-card" key={category}>
      <label htmlFor={`category-textarea-${category}`} className="question-label">
        {CATEGORY_QUESTIONS[category]}
      </label>
      <textarea
        id={`category-textarea-${category}`}
        name={`category-textarea-${category}`}
        value={categoryAnswers[category] || ''}
        onChange={(e) => handleCategoryAnswerChange(category, e.target.value)}
        required
        placeholder="Type your answer here"
        className="feedback-textarea"
        maxLength={1000}
        aria-describedby={`counter-${category}`}
      />
      <div className="char-counter" id={`counter-${category}`}>
        {categoryAnswers[category]?.length || 0}/1000
      </div>
    </div>
  ))}
  {/* Always show the other feedback textarea, last */}
  <div className="form-group feedback-card">
    <label htmlFor="other-feedback" className="question-label">{OTHER_FEEDBACK_QUESTION}</label>
    <textarea
      id="other-feedback"
      name="other-feedback"
      value={otherFeedback}
      onChange={handleOtherFeedbackChange}
      required={formData.categories.length === 0}
      placeholder="Type your answer here"
      className="feedback-textarea"
      maxLength={1000}
      aria-describedby="counter-other"
    />
    <div className="char-counter" id="counter-other">
      {otherFeedback.length}/1000
    </div>
  </div>
            <div className="form-group">
              <label htmlFor="email"><b>Please provide your email address:*</b></label>
              <div className="feedback-email-note">*We will only use this information to contact you regarding your feedback</div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Email Address"
                className="feedback-input"
              />
            </div>
            {submitStatus === 'success' && (
              <div className="feedback-success">
                <h3>Thank you for your feedback!</h3>
                <p>We will review it shortly and get back to you if needed.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="feedback-error">
                <h3>Something went wrong</h3>
                <p>Please try again or contact support at tdis@tamu.edu</p>
              </div>
            )}
            <div className="feedback-actions feedback-actions-centered">
              <button
                type="submit"
                className="feedback-submit feedback-submit-wide"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'SUBMIT MY FORM'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm; 